'use client';

import type { ReactNode } from 'react';
import {
  Alert01Icon,
  BookOpen01Icon,
  Globe02Icon,
  PlayIcon,
  RadioIcon,
  FlashIcon,
} from '@terra/ui-icons';
import type {
  ChatObjectNavigationTarget,
  OnOpenChatObject,
} from '../../hitl-types';
import { InlineObjectReferenceLink } from './InlineObjectReferenceLink';

export type PortalObjectType =
  | 'finding'
  | 'signal'
  | 'endpoint'
  | 'webpage'
  | 'execution'
  | 'skill-draft';
type PortalObjectReferenceCardProps = {
  objectType: PortalObjectType;
  objectId: string;
  title?: string | null;
  prefix?: string;
  onOpenObject?: OnOpenChatObject;
  navigationTarget?: ChatObjectNavigationTarget;
};
type PortalObjectReviewStyle = {
  icon: ReactNode;
  iconColor: string;
  fallbackTitle: string;
};
const PORTAL_OBJECT_REVIEW_STYLES: Record<
  PortalObjectType,
  PortalObjectReviewStyle
> = {
  finding: {
    icon: <Alert01Icon size={14} />,
    iconColor: 'fg.vulnerability.vulnerable',
    fallbackTitle: 'Generated finding',
  },
  signal: {
    icon: <RadioIcon size={14} />,
    iconColor: 'fg.vulnerability.vulnerable',
    fallbackTitle: 'Generated signal',
  },
  endpoint: {
    icon: <FlashIcon size={14} />,
    iconColor: 'colorPalette.default',
    fallbackTitle: 'Endpoint',
  },
  webpage: {
    icon: <Globe02Icon size={14} />,
    iconColor: 'colorPalette.default',
    fallbackTitle: 'Web Page',
  },
  execution: {
    icon: <PlayIcon size={14} />,
    iconColor: 'colorPalette.default',
    fallbackTitle: 'Execution',
  },
  'skill-draft': {
    icon: <BookOpen01Icon size={14} />,
    iconColor: 'colorPalette.default',
    fallbackTitle: 'Skill draft',
  },
};

export function PortalObjectReferenceCard({
  objectType,
  objectId,
  title,
  prefix,
  onOpenObject,
  navigationTarget,
}: PortalObjectReferenceCardProps) {
  const canOpenObject = Boolean(onOpenObject);
  const reviewStyle = PORTAL_OBJECT_REVIEW_STYLES[objectType];
  const displayTitle =
    title?.trim() ||
    (objectType === 'execution'
      ? `${reviewStyle.fallbackTitle} available`
      : reviewStyle.fallbackTitle);
  const handleOpenObject = () => {
    if (!onOpenObject) {
      return;
    }

    const target =
      navigationTarget ??
      (objectType === 'skill-draft'
        ? null
        : {
            type: objectType,
            id: objectId,
            label: displayTitle,
          });

    if (!target) {
      return;
    }

    onOpenObject(target);
  };

  return (
    <InlineObjectReferenceLink
      prefix={prefix}
      name={displayTitle}
      icon={reviewStyle.icon}
      iconColor={reviewStyle.iconColor}
      onClick={canOpenObject ? handleOpenObject : undefined}
      title={displayTitle}
    />
  );
}
